'use client';

import { Icon } from '@/src/shared/ui/icons/Icon';
import { cn } from '@/src/shared/lib/utils';
import type { ConnectionStatus } from '../types';

type WidgetState = 'collapsed' | 'chatsList' | 'activeChat';

interface MiniChatHeaderProps {
  state: WidgetState;
  title: string;
  unreadCount?: number;
  onExpand: () => void;
  onCollapse: () => void;
  onExtend: () => void;
  onBack?: () => void;
  connectionStatus?: ConnectionStatus;
}

/**
 * Mini Chat Header - Header for the floating chat widget
 *
 * Adapts to different states:
 * - collapsed: Shows logo, "Support" title, unread badge, close button
 * - chatsList: Shows "Chats" title, unread badge, minimize, expand, close buttons
 * - activeChat: Shows back button, chat name, minimize, expand, close buttons
 */
export const MiniChatHeader = ({
  state,
  title,
  unreadCount = 0,
  onExpand,
  onCollapse,
  onExtend,
  onBack,
  connectionStatus,
}: MiniChatHeaderProps) => {
  const isCollapsed = state === 'collapsed';
  const isActiveChat = state === 'activeChat';

  return (
    <div
      className={cn(
        'h-20 px-5 py-4 bg-accent-secondary',
        'flex justify-between items-center',
        'rounded-t-[30px]',
        isCollapsed && 'cursor-pointer hover:bg-accent-secondary/90 transition-colors'
      )}
      onClick={isCollapsed ? onExpand : undefined}
    >
      {/* Left side */}
      <div className="flex items-center gap-2.5">
        {/* Back button for active chat */}
        {isActiveChat && onBack && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBack();
            }}
            className="w-12 h-12 flex items-center justify-center hover:opacity-70 transition-opacity"
            aria-label="Back to chat list"
          >
            <Icon name="arrow-left" size="md" color="inverse" />
          </button>
        )}

        {/* Logo for collapsed state */}
        {isCollapsed && (
          <div className="w-12 h-12 flex items-center justify-center">
            <Icon name="variant32" size="2xl" color="inverse" />
          </div>
        )}

        {/* Title */}
        <span className="text-text-inverse text-2xl font-semibold font-['Roboto'] leading-8">
          {isCollapsed ? 'Support' : title}
        </span>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <div className="h-6 min-w-6 px-1 bg-feedback-attention rounded-[200px] flex flex-col justify-center items-center">
            <span className="text-text-inverse text-base font-extrabold font-['Roboto'] leading-5">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </div>
        )}

        {/* Connection status indicator */}
        {!isCollapsed && connectionStatus && connectionStatus !== 'connected' && (
          <div
            className={cn(
              'w-2 h-2 rounded-full',
              connectionStatus === 'connecting' && 'bg-yellow-400 animate-pulse',
              connectionStatus === 'disconnected' && 'bg-red-400'
            )}
            title={connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
          />
        )}
      </div>

      {/* Right side - Action buttons */}
      <div className="flex items-center gap-1">
        {!isCollapsed && (
          /* Expand/fullscreen button */
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExtend();
            }}
            className="w-12 h-12 flex items-center justify-center hover:opacity-70 transition-opacity"
            aria-label="Open full chat"
          >
            <Icon name="extend" size="md" color="inverse" />
          </button>
        )}

        {/* Minimize/Close button - closes the mini chat */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCollapse();
          }}
          className="w-12 h-12 flex items-center justify-center hover:opacity-70 transition-opacity"
          aria-label="Close chat"
        >
          <Icon name="variant31" size="md" color="inverse" />
        </button>
      </div>
    </div>
  );
};
